import React from 'react';
import { Image, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * ExportImageDialog - Dialog for configuring deck image export options
 * shadcn/ui version
 */
export default function ExportImageDialogShadcn({
  open,
  onOpenChange,
  onExport,
  exportOptions,
  onOptionsChange,
  loading = false,
}) {
  const { t } = useLanguage();

  const handleCardsPerRowChange = (e) => {
    const value = Math.max(3, Math.min(10, parseInt(e.target.value) || 5));
    onOptionsChange({ ...exportOptions, cardsPerRow: value });
  };

  const handleRowsChange = (e) => {
    const value = Math.max(1, Math.min(15, parseInt(e.target.value) || 6));
    onOptionsChange({ ...exportOptions, numberOfRows: value });
  };

  const totalCards = exportOptions.cardsPerRow * exportOptions.numberOfRows;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('exportImage.title', 'Export Image Options')}
          </DialogTitle>
          <DialogDescription>
            Configure the layout for your deck image export.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          {/* Cards Per Row */}
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="cardsPerRow" className="text-sm font-medium min-w-[120px]">
              {t('exportImage.cardsPerRow', 'Cards Per Row:')}
            </label>
            <Input
              id="cardsPerRow"
              type="number"
              className="max-w-[120px]"
              value={exportOptions.cardsPerRow}
              onChange={handleCardsPerRowChange}
              min={3}
              max={10}
              step={1}
            />
          </div>

          {/* Number of Rows */}
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="numberOfRows" className="text-sm font-medium min-w-[120px]">
              {t('exportImage.numberOfRows', 'Number of Rows:')}
            </label>
            <Input
              id="numberOfRows"
              type="number"
              className="max-w-[120px]"
              value={exportOptions.numberOfRows}
              onChange={handleRowsChange}
              min={1}
              max={15}
              step={1}
            />
          </div>

          {/* Preview */}
          <div className="text-center p-3 bg-muted rounded-md">
            <span className="text-sm text-muted-foreground">
              {t('exportImage.layout', 'Layout:')} {exportOptions.cardsPerRow} × {exportOptions.numberOfRows} = {totalCards} {t('common.cards', 'cards')}
            </span>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={onExport} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('controls.generating', 'Generating...')}
              </>
            ) : (
              <>
                <Image className="h-4 w-4 mr-2" />
                {t('exportImage.generate', 'Generate Image')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
